import { useEffect, useLayoutEffect, useState } from "react";
import { waitForPaint } from "../utils/waitForPaint";

function shouldRequirePanelRouteReady(routeKey: string): boolean {
  return (
    !routeKey.startsWith("/auth") &&
    !routeKey.startsWith("/rtl") &&
    !routeKey.startsWith("/demo/entry-analysis")
  );
}

function hasMountedAppContent(routeKey: string): boolean {
  const root = document.getElementById("root");
  if (!root || root.childElementCount === 0) {
    return false;
  }

  if (!document.querySelector("[data-app-route-surface]")) {
    return false;
  }

  if (!shouldRequirePanelRouteReady(routeKey)) {
    return true;
  }

  // Require the dashboard panel shell to exist, but do **not** gate on
  // `data-app-route-view-ready`. That attribute is flipped asynchronously after paint;
  // combined with query-only `location.search` updates it could leave `pageContentLoaded`
  // stuck false (endless loader) or race the route loader vs real content (blank screen).
  return Boolean(document.querySelector("[data-app-panel-route-host]"));
}

function isDocumentReady(routeKey: string): boolean {
  return document.readyState === "complete" && hasMountedAppContent(routeKey);
}

/**
 * True once the document is complete and React has painted route content.
 * Resets when `routeKey` changes (caller should pass a stable key, e.g. pathname only).
 */
export function usePageContentReady(routeKey: string): boolean {
  const [pageContentLoaded, setPageContentLoaded] = useState(false);

  useLayoutEffect(() => {
    setPageContentLoaded(false);
  }, [routeKey]);

  useEffect(() => {
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const markLoaded = () => {
      void waitForPaint().then(() => {
        if (!cancelled) {
          setPageContentLoaded(true);
        }
      });
    };

    const checkReady = () => {
      if (cancelled || !isDocumentReady(routeKey)) {
        return;
      }
      // Require the ready markers to survive two more animation frames so we do not
      // dismiss the route loader on a transient paint (e.g. empty panel before auth shell).
      requestAnimationFrame(() => {
        if (cancelled || !isDocumentReady(routeKey)) {
          return;
        }
        requestAnimationFrame(() => {
          if (cancelled || !isDocumentReady(routeKey)) {
            return;
          }
          if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
          }
          markLoaded();
        });
      });
    };

    intervalId = setInterval(checkReady, 100);
    checkReady();

    const fallbackTimer = window.setTimeout(() => {
      if (cancelled) return;
      if (document.querySelector("[data-app-route-surface]")) {
        markLoaded();
      }
    }, 8000);

    return () => {
      cancelled = true;
      if (intervalId) {
        clearInterval(intervalId);
      }
      window.clearTimeout(fallbackTimer);
    };
  }, [routeKey]);

  return pageContentLoaded;
}
